<template>
  <div class="sf-gallery">
    <div class="sf-gallery__stage">
      <div class="product-gallery-controls">
        <button
          @click="go(activeIndex - 1)"
          class="product-gallery-control"
          :class="activeIndex === 0 && 'product-gallery-control__disabled'"
        >
          <SvgArrow
            color="var(--c-grey-2)"
            direction="left"
            :width="deviceSize.isMobile && !deviceSize.isMobile ? 44 : 30"
            :height="deviceSize.isMobile && !deviceSize.isMobile ? 44 : 30"
          />
        </button>
        <button
          @click="go(activeIndex + 1)"
          class="product-gallery-control"
          :class="
            activeIndex + 1 === this.images.length &&
            'product-gallery-control__disabled'
          "
        >
          <SvgArrow
            color="var(--c-grey-2)"
            direction="right"
            :width="deviceSize.isMobile && !deviceSize.isMobile ? 44 : 30"
            :height="deviceSize.isMobile && !deviceSize.isMobile ? 44 : 30"
          />
        </button>
      </div>
      <div class="product-gallery-counter">
        <CustomGalleryCounterIcon class="product-gallery-counter__icon" />
        <span class="product-gallery-counter__text">
          <strong>{{ activeIndex + 1 }}</strong>
          <span>{{ $t('of') }}</span>
          <strong>{{ images.length }}</strong>
        </span>
      </div>
      <div ref="glide" class="glide">
        <div class="glide__track" data-glide-el="track">
          <ul class="glide__slides" @click="isZoomStarted = !isZoomStarted">
            <li
              v-for="(picture, index) in images"
              :key="'slide-' + index"
              class="glide__slide"
            >
              <nuxt-img
                ref="sfGalleryBigImage"
                class="sf-gallery__big-image"
                :class="{ 'sf-gallery__big-image--has-zoom': enableZoom }"
                :src="picture.desktop.url"
                :alt="getBigImage(index)"
                :title="getBigImage(index)"
                placeholder="/assets/product_placeholder.svg"
                :width="imageWidth"
                :height="imageHeight"
                :image-tag="imageTag"
                :nuxt-img-config="nuxtImgConfig"
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="sf-gallery__thumbs">
      <slot name="thumbs" v-bind="{ images, active: activeIndex, go }">
        <SfButton
          v-for="(image, index) in images"
          :key="'img-' + index"
          class="sf-button--pure sf-gallery__item"
          :class="{ 'sf-gallery__item--selected': index === activeIndex }"
          :aria-label="'Image ' + index"
          @click="go(index)"
        >
          <nuxt-img
            class="sf-gallery__thumb"
            :src="image.mobile.url"
            :alt="getThumbImage(index)"
            :title="getThumbImage(index)"
            placeholder="/assets/product_placeholder.svg"
            :width="thumbWidth"
            :height="thumbHeight"
            :image-tag="thumbImageTag"
            :nuxt-img-config="thumbNuxtImgConfig"
            :ref="`thumb-${index}`"
          />
        </SfButton>
      </slot>
    </div>
    <SfModal
      :visible="isZoomStarted"
      class="product-image-gallery-zoom"
      @click="isZoomStarted = isZoomStarted = !isZoomStarted"
      @close="isZoomStarted = isZoomStarted = !isZoomStarted"
    >
      <nuxt-img
        ref="sfGalleryBigImage"
        :src="images[activeIndex].desktop.url"
        :alt="getZoomImage()"
        :title="getZoomImage()"
        placeholder="/assets/product_placeholder.svg"
        :width="imageWidth"
        :height="imageHeight"
        :image-tag="imageTag"
        :nuxt-img-config="nuxtImgConfig"
        @click="
          () => {
            if (deviceSize && deviceSize.isDesktop) {
              isZoomStarted = !isZoomStarted;
            }
          }
        "
      />
    </SfModal>
  </div>
</template>

<script>
import Glide from '@glidejs/glide';
import { SfButton, SfModal } from '@storefront-ui/vue';
import CustomGalleryCounterIcon from '@/components/Catalog/Product/CustomGalleryCounterIcon.vue';
import { useContext } from '@nuxtjs/composition-api';
import SvgArrow from '~/components/General/SvgArrow.vue';

export default {
  name: 'CustomGallery',
  components: {
    SfButton,
    CustomGalleryCounterIcon,
    SvgArrow,
    SfModal,
  },
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    imageWidth: {
      type: [Number, String],
      default: null,
    },
    imageHeight: {
      type: [Number, String],
      default: null,
    },
    thumbWidth: {
      type: [Number, String],
      default: null,
    },
    thumbHeight: {
      type: [Number, String],
      default: null,
    },
    current: {
      type: Number,
      default: 1,
    },
    product: {
      type: Object,
      required: false,
    },
    sliderOptions: {
      type: Object,
      default() {
        return {
          type: 'slider',
          autoplay: false,
          rewind: false,
          gap: 0,
        };
      },
    },
    outsideZoom: {
      type: Boolean,
      default: false,
    },
    enableZoom: {
      type: Boolean,
      default: false,
    },
    imageTag: {
      type: String,
      default: 'nuxt-img',
    },
    nuxtImgConfig: {
      type: Object,
      default: () => ({}),
    },
    thumbImageTag: {
      type: String,
      default: 'nuxt-img',
    },
    thumbNuxtImgConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      positionStatic: {},
      eventHover: {},
      glide: null,
      activeIndex: this.current - 1,
      style: '',
      pictureSelected: this.images[0] || {
        alt: '',
        zoom: '',
        big: {
          url: '',
        },
        desktop: '',
        placeholder: '/assets/product_placeholder.svg',
      },
      isZoomStarted: false,
    };
  },
  computed: {
    mapPictures() {
      // map images to handle picture tags with SfImage
      return this.images.map(({ desktop, big }) => ({
        mobile: desktop,
        desktop: big,
      }));
    },
    updatedSliderOptions() {
      return { ...this.sliderOptions, startAt: this.activeIndex };
    },
    definedPicture() {
      const { zoom, big, desktop, alt, placeholder } = this.pictureSelected;
      const definedPicture = zoom || big || desktop;
      definedPicture ? (definedPicture.alt = alt) : null;
      definedPicture ? (definedPicture.placeholder = placeholder) : null;
      return definedPicture || '';
    },
  },
  mounted() {
    this.$nextTick(() => {
      // handle slider with swipe and transitions with Glide.js
      // https://glidejs.com/docs/
      if (this.images.length === 0) return;
      const glide = new Glide(this.$refs.glide, this.updatedSliderOptions);
      glide.on('run', () => {
        this.go(glide.index);
      });
      glide.mount();
      this.glide = glide;
    });
  },
  updated() {
    if (this.glide) {
      this.$nextTick(() => {
        this.glide.mount();
      });
    }
  },
  beforeUnmount() {
    if (this.glide) {
      this.glide.destroy();
    }
  },
  setup() {
    const { app } = useContext();
    return {
      deviceSize: app.$device,
    };
  },
  methods: {
    positionObject(index) {
      if (this.$refs.sfGalleryBigImage) {
        if (this.outsideZoom) {
          return this.$refs.glide.getBoundingClientRect();
        }
        return this.$refs.sfGalleryBigImage[index].$el.getBoundingClientRect();
      }
      return '';
    },
    go(index) {
      this.pictureSelected = this.images[index];
      if (!this.glide) return;
      this.activeIndex = index;
      /**
       * Event for current image change (`v-model`)
       * @type {Event}
       */
      this.$emit('click', +index + 1);
      if (this.glide) {
        this.glide.go(`=${index}`);
      }

      let thumbElement = null;
      try {
        thumbElement = this.$refs[`thumb-${index}`][0].$el;
      } catch {
        console.warn('Could not find thumb to scroll to.');
      }

      if (thumbElement) {
        thumbElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    },
    startZoom() {
      if (this.enableZoom) {
        this.isZoomStarted = true;
      }
    },
    moveZoom($event, index) {
      if (this.enableZoom) {
        this.eventHover = $event;
        if (this.outsideZoom) {
          this.positionStatic = this.positionObject(index);
          this.$refs.outSide.style.cssText = `position: absolute; left: ${
            $event.clientX - this.positionStatic.x
          }px; top: ${$event.clientY - this.positionStatic.y}px; z-index: 1;`;
          this.$refs.imgZoom.$el.children[0].style.cssText = `transform: scale(2); width: 300px; height: auto;`;
          this.$refs.imgZoom.$el.children[0].style.transformOrigin = `${
            $event.clientX - this.positionStatic.x
          }px ${$event.clientY - this.positionStatic.y}px`;
        } else {
          this.positionStatic = this.positionObject(index);
          this.$refs.sfGalleryBigImage[index].$el.children[0].style.cssText =
            'top: 0; transform: scale(2);';
          this.$refs.sfGalleryBigImage[
            index
          ].$el.children[0].style.transformOrigin = `${
            $event.clientX - this.positionStatic.x
          }px ${$event.clientY - this.positionStatic.y}px`;
        }
      }
    },
    removeZoom(index) {
      if (this.enableZoom) {
        this.isZoomStarted = false;
        if (this.outsideZoom) return;
        this.$refs.sfGalleryBigImage[index].$el.children[0].style.transform =
          'scale(1)';
      }
    },
    getBigImage(index, picture) {
      if (this?.$props.product) {
        return `${this?.$props.product?.name}_${this?.$props.product?.brand?.label}_${index}_big-image`;
      }
      return picture?.alt;
    },
    getThumbImage(index, image) {
      if (this?.$props.product) {
        return `${this?.$props.product?.name}_${this?.$props.product?.brand?.label}_${index}_thumb-image`;
      }
      return image?.alt;
    },
    getZoomImage() {
      if (this?.$props.product) {
        return `${this?.$props.product?.name}_${this?.$props.product?.brand?.label}_zoom-image`;
      }
      return this?.$props.images[this?.activeIndex]?.alt;
    },
  },
};
</script>

<style lang="scss">
@import '~@storefront-ui/shared/styles/components/molecules/SfGallery.scss';
.product-gallery-controls {
  display: flex;
  justify-content: space-between;
  position: absolute;
  width: 100%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  @media (max-width: $tablet-max) {
    display: none;
  }
  .product-gallery-control {
    box-shadow: none;
    padding: 0;
    width: 60px;
    height: 60px;
    border: 1px solid var(--c-grey-2);
    color: var(--c-grey-2);
    border-radius: 3px;
    background-color: transparent;
    z-index: 9;
    width: 44px;
    height: 44px;

    &:hover {
      background-color: var(--c-grey-9);
      cursor: pointer;
    }
    &__disabled {
      pointer-events: none;
      opacity: 0.1;
    }
    margin: 0 10px;
  }
}

.sf-gallery.product__gallery {
  .sf-gallery__stage {
    .glide {
      &__slide {
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
    }
  }
}
.product-gallery-counter {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--c-grey-3);
  position: absolute;
  bottom: 10px;
  justify-content: center;
  width: 100%;
  &__icon {
    width: 27px;
    height: 24px;
    path {
      fill: var(--c-grey-3);
    }
  }
  &__text {
    font-family: var(--font-family--secondary-bold);
  }
}

.product-image-gallery-zoom {
  width: 100vw;
  height: auto;

  .sf-modal__container {
    width: 100%;
    height: auto;
    z-index: 8;
    display: flex;
    justify-content: center;
    align-items: center;
    @media (max-width: $tablet-max) {
      img {
        max-width: 100vw;
      }
    }
    .sf-modal__bar.smartphone-only {
      box-shadow: 0 10px 35px var(--c-transparent-white);
      border-radius: 15px;
      background: var(--c-white) 0 0 no-repeat padding-box;
    }
  }
}

.sf-gallery {
  &__thumbs {
    .sf-image--wrapper {
      scale: 0.7;
    }
  }
}
</style>
